/**
 * Hook para verificar el estado del trial y manejar la expiración
 */
import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSimpleEmpresa } from '@/hooks/useSimpleEmpresa';
import { usePlanes } from './usePlanes';
import { getTrialConfig } from '@/services/configuracionGlobalService';

export const useTrialGuard = () => {
  // Usar SimpleEmpresa directamente
  const { empresaActual } = useSimpleEmpresa();
  
  const { planes } = usePlanes();
  const [trialConfig, setTrialConfig] = useState(null);
  const location = useLocation();
  
  // Verificar si es plan gratuito temprano para evitar procesamientos innecesarios
  const isFreePlan = useMemo(() => {
    if (!empresaActual || !planes.length) return false;
    return empresaActual.plan_id === 'revvly_free' || 
           planes.find(p => p.id === empresaActual.plan_id)?.price === 0;
  }, [empresaActual?.plan_id, planes]);

  // Verificar si estamos en onboarding
  const isOnboarding = location.pathname.includes('/onboarding') ||
                      location.pathname.startsWith('/taller') ||
                      location.pathname.startsWith('/cliente') ||
                      localStorage.getItem('revvly_user_type') === 'taller';

  // Cargar configuración de trial - SOLO si es plan gratuito y no estamos en onboarding
  useEffect(() => {
    // Si no es plan gratuito, no cargar configuración
    if (!isFreePlan) {
      setTrialConfig({
        trialDays: 30,
        warningDays: 7,
        graceDays: 7,
        enabled: false
      });
      return;
    }
    
    if (isOnboarding) {
      console.log('🚫 [useTrialGuard] Saltando carga de configuración - estamos en onboarding:', location.pathname);
      // Usar valores por defecto sin hacer llamadas API
      setTrialConfig({
        trialDays: 30,
        warningDays: 7,
        graceDays: 7,
        enabled: true
      });
      return;
    }

    const loadTrialConfig = async () => {
      try {
        console.log('⚙️ [useTrialGuard] Cargando configuración de trial para plan gratuito:', location.pathname);
        const config = await getTrialConfig();
        setTrialConfig(config);
      } catch (error) {
        console.error('❌ Error loading trial config:', error);
        // Usar valores por defecto
        setTrialConfig({
          trialDays: 30,
          warningDays: 7,
          graceDays: 7,
          enabled: true
        });
      }
    };

    loadTrialConfig();
  }, [isFreePlan, isOnboarding, location.pathname]);

  const trialStatus = useMemo(() => {
    // Si no es plan gratuito, retornar inmediatamente sin procesamiento
    if (!isFreePlan) {
      return {
        isFreePlan: false,
        isTrialExpired: false,
        daysRemaining: 0,
        shouldBlockAccess: false
      };
    }
    
    // Siempre retornar valores por defecto seguros para evitar bloqueos
    if (!empresaActual || !planes.length || !trialConfig) {
      return {
        isFreePlan: true,
        isTrialExpired: false,
        daysRemaining: 30,
        shouldBlockAccess: false
      };
    }

    // Calcular días desde fecha_registro usando configuración
    const fechaRegistro = new Date(empresaActual.fecha_registro);
    const ahora = new Date();
    const diasTranscurridos = Math.floor((ahora - fechaRegistro) / (1000 * 60 * 60 * 24));
    const diasTrial = trialConfig.trialDays;
    const diasRestantes = Math.max(diasTrial - diasTranscurridos, 0);
    
    const isTrialExpired = diasRestantes === 0;
    const shouldBlockAccess = isTrialExpired;

    console.log('🛡️ Trial Guard Status:', {
      isFreePlan,
      isTrialExpired,
      diasRestantes,
      diasTrial,
      shouldBlockAccess,
      fechaRegistro: empresaActual.fecha_registro
    });

    return {
      isFreePlan: true,
      isTrialExpired,
      daysRemaining: diasRestantes,
      shouldBlockAccess,
      registrationDate: fechaRegistro
    };
  }, [isFreePlan, empresaActual?.plan_id, empresaActual?.fecha_registro, planes.length, trialConfig]);

  return trialStatus;
};

export default useTrialGuard;